import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { getUsers, deleteUser, updateUser, createUser, resetUserPassword } from "../../../utils/api";

const columns = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { 
    key: "role", 
    label: "Role",
    render: (value) => (
      <span className={`role-badge role-${value?.toLowerCase()}`}>
        {value || "User"}
      </span>
    )
  },
  { 
    key: "birthDate", 
    label: "Birth Date",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
  { 
    key: "createdOn", 
    label: "Created On",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
  { 
    key: "updatedAt", 
    label: "Updated On",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
];

const filters = [
  {
    key: "role",
    label: "Filter by Role",
    type: "select",
    options: [
      { value: "admin", label: "Admin" },
      { value: "manager", label: "Manager" },
      { value: "user", label: "User" },
    ]
  },
  {
    key: "createdOn",
    label: "Created Date Range",
    type: "dateRange"
  },
  {
    key: "updatedAt",
    label: "Updated Date Range",
    type: "dateRange"
  },
];

// Password Input Component with visibility toggle
const PasswordInput = ({ value, onChange, name, placeholder, show, setShow, required = false }) => (
  <div style={{ position: "relative" }}>
    <input
      type={show ? "text" : "password"}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      style={{ width: "100%", paddingRight: "40px" }}
    />
    <span
      onClick={() => setShow(!show)}
      style={{
        position: "absolute",
        right: "10px",
        top: "50%",
        transform: "translateY(-50%)",
        cursor: "pointer",
        color: "#666"
      }}
    >
      {show ? <FaEyeSlash /> : <FaEye />}
    </span>
  </div>
);

export default function Users() {
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "user",
    password: "",
    birthDate: "",
    birthPlace: {
      city: "",
      country: ""
    },
    profilePicture: ""
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const handleAdd = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      role: "user",
      password: "",
      birthDate: "",
      birthPlace: { city: "", country: "" },
      profilePicture: ""
    });
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setPasswordError("");
    setSelectedUser(null);
    setEditModalOpen(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      role: user.role || "user",
      password: "",
      birthDate: user.birthDate ? new Date(user.birthDate).toISOString().split('T')[0] : "",
      birthPlace: user.birthPlace || { city: "", country: "" },
      profilePicture: user.profilePicture || ""
    });
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setPasswordError("");
    setEditModalOpen(true);
  };

  const handleView = (user) => {
    handleEdit(user);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowNewPassword(false);
    setPasswordModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("birthPlace.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({ 
        ...prev, 
        birthPlace: { ...prev.birthPlace, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (name === "password" || name === "confirmPassword") {
      setPasswordError("");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, profilePicture: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validatePasswords = () => {
    if (!selectedUser && !formData.password) {
      setPasswordError("Password is required for new users");
      return false;
    }
    if (formData.password && formData.password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    if (formData.password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (selectedUser) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await updateUser(selectedUser._id, updateData);
        alert("User updated successfully!");
      } else {
        if (!validatePasswords()) {
          setLoading(false);
          return;
        }
        await createUser(formData);
        alert("User created successfully!");
      }
      setEditModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      alert("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await resetUserPassword(selectedUser._id, newPassword);
      alert("Password reset successfully!");
      setPasswordModalOpen(false);
      setNewPassword("");
    } catch (error) {
      alert(error.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const customActions = (user) => (
    <button
      className="reset-password-btn"
      onClick={() => handleResetPassword(user)}
      title="Reset Password"
      style={{
        backgroundColor: "#FF9800",
        color: "white",
        padding: "6px 10px",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "12px"
      }}
    >
      Reset PW
    </button>
  );

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Users"
          fetchData={getUsers}
          deleteData={deleteUser}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          customActions={customActions}
          searchPlaceholder="Search users by name or email..."
        />
      </main>

      {/* Edit/Create User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedUser ? "Edit User" : "Add New User"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Role *</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleFormChange}
                required
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
              </select>
            </div>
            <div className="form-group">
              <label>Birth Date</label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleFormChange}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Location</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Birth City</label>
                <input
                  type="text"
                  name="birthPlace.city"
                  value={formData.birthPlace.city}
                  onChange={handleFormChange}
                  placeholder="City"
                />
              </div>
              <div className="form-group">
                <label>Birth Country</label>
                <input
                  type="text"
                  name="birthPlace.country"
                  value={formData.birthPlace.country}
                  onChange={handleFormChange}
                  placeholder="Country"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Profile Picture</h3>
            <div className="form-group">
              <label>Profile Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.profilePicture && (
                <div className="image-preview">
                  <img src={formData.profilePicture} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, profilePicture: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>Security</h3>
            <div className="form-group">
              <label>
                Password {!selectedUser && "*"}
                {selectedUser && <span style={{ fontWeight: "normal", fontSize: "12px", color: "#666" }}> (leave empty to keep current)</span>}
              </label>
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={handleFormChange}
                placeholder={selectedUser ? "Enter new password" : "Enter password"}
                show={showPassword}
                setShow={setShowPassword}
                required={!selectedUser}
              />
            </div>
            
            {!selectedUser && (
              <div className="form-group">
                <label>Confirm Password *</label>
                <PasswordInput
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordError("");
                  }}
                  placeholder="Confirm password"
                  show={showConfirmPassword}
                  setShow={setShowConfirmPassword}
                  required
                />
              </div>
            )}
            
            {passwordError && (
              <div style={{ color: "red", fontSize: "14px", marginBottom: "15px" }}>
                {passwordError}
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        title="Reset Password"
        size="small"
      >
        <form onSubmit={handlePasswordSubmit}>
          <div className="form-group">
            <label>User</label>
            <input
              type="text"
              value={`${selectedUser?.firstName} ${selectedUser?.lastName}`}
              disabled
              style={{ backgroundColor: "#f5f5f5" }}
            />
          </div>
          <div className="form-group">
            <label>New Password *</label>
            <PasswordInput
              name="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              show={showNewPassword}
              setShow={setShowNewPassword}
              required
            />
            <small style={{ color: "#666", fontSize: "12px" }}>
              Password must be at least 6 characters
            </small>
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setPasswordModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
